import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { BriefingSections, TeamTodaySurfaceData } from './useTeamTodaySurface';

// Mirrors the BFF briefing-history contract (Terum-MVP lib/dashboard/contracts/
// briefing-history.ts). List rows are light (a `preview` instead of full sections);
// the detail returns the full as-generated sections. `status` is intentionally `string`
// (not a 'ready' | 'empty' union) to mirror the backend's deliberate `z.string()`:
// daily_briefings.status is permissive across historical rows, and toSurfaceData
// normalizes it ('empty' -> empty state, anything else -> 'ready'). Narrowing it here
// would make this mirror STRICTER than the contract — that is the invariant-#6 drift,
// not the current `string`.

export interface BriefingHistoryListItem {
  id: string;
  team_id: string;
  team_name: string | null;
  briefing_date: string;
  status: string;
  generated_at: string | null;
  viewed_at: string | null;
  preview: string | null;
}

export interface BriefingHistoryListData {
  briefings: BriefingHistoryListItem[];
  total_count: number;
}

export interface BriefingHistoryDetailData {
  id: string;
  team_id: string;
  team_name: string | null;
  briefing_date: string;
  status: string;
  generated_at: string | null;
  viewed_at: string | null;
  // As-generated sections; reuses the team/today renderer's shape.
  data: BriefingSections | null;
}

export function useBriefingHistory() {
  const query = useQuery({
    queryKey: ['surfaces', 'briefing-history'],
    queryFn: () => apiFetch<BriefingHistoryListData>('/api/surfaces/briefings/history'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return { ...query, data: query.data ?? undefined };
}

export function useBriefingHistoryDetail(id: string) {
  const query = useQuery({
    queryKey: ['surfaces', 'briefing-history', id],
    queryFn: () =>
      apiFetch<BriefingHistoryDetailData>(
        `/api/surfaces/briefings/history/${encodeURIComponent(id)}`,
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id,
  });
  return { ...query, data: query.data ?? undefined };
}

/**
 * Adapt a history detail row into the shape the TeamTodaySurface renderer
 * consumes. The BFF only emits 'ready' or 'empty'; anything else maps to 'ready'
 * and the renderer's null-sections guard handles the empty case.
 */
export function toSurfaceData(detail: BriefingHistoryDetailData): TeamTodaySurfaceData {
  return {
    status: detail.status === 'empty' ? 'empty' : 'ready',
    data: detail.data,
    generated_at: detail.generated_at,
    viewed_at: detail.viewed_at,
    // A historical briefing is shown on its own date; it is never the "yesterday"
    // fallback that the today surface uses is_previous for. Set explicitly so the
    // renderer never shows a misleading "yesterday" kicker for a past briefing.
    is_previous: false,
  };
}
